import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Filter from "./Filter";
import ActiveFilters from "./ActiveFilters";
import AnalyticsModal from "./AnalyticsModal";
import PropertyDetails from "./PropertyDetails";
import PropertyPreview from "./PropertyPreview";
import { agents, counties, propertyTypes } from "./filterOptions";
import {
  API,
  DATASETS,
  DEFAULT_FILTERS,
  addressFor,
  comparisonColor,
  comparisonLabel,
  fetchJSON,
  money,
  numeric,
  parseSelection,
  priceColor,
  queryForFilters,
  spreadPoints,
  validFeatures,
  validateFilters,
} from "./propertyData";

function initialFilters() {
  const params = new URLSearchParams(window.location.search);
  const filters = { ...DEFAULT_FILTERS };
  for (const [key, param, options] of [
    ["filterCounties", "counties", counties],
    ["filterAgents", "agents", agents],
    ["filterPropertyTypes", "propertytypes", propertyTypes],
  ]) {
    filters[key] = parseSelection(params.get(param), options);
  }
  for (const key of [
    "startYear",
    "endYear",
    "minBeds",
    "maxBeds",
    "minPrice",
    "maxPrice",
  ]) {
    if (params.has(key.toLowerCase()))
      filters[key] = params.get(key.toLowerCase());
  }
  if (validateFilters(filters))
    Object.assign(
      filters,
      Object.fromEntries(
        [
          "startYear",
          "endYear",
          "minBeds",
          "maxBeds",
          "minPrice",
          "maxPrice",
        ].map((key) => [key, DEFAULT_FILTERS[key]])
      )
    );
  if (Object.prototype.hasOwnProperty.call(DATASETS, params.get("dataoption")))
    filters.dataOption = params.get("dataoption");
  return filters;
}
function initialView() {
  const params = new URLSearchParams(window.location.search);
  const coordinate = (key, fallback, min, max) => {
    const n = numeric(params.get(key));
    return n !== null && n >= min && n <= max ? n : fallback;
  };
  return {
    lat: coordinate("lat", 53.4, -90, 90),
    lng: coordinate("lng", -7.9, -180, 180),
    zoom: coordinate("zoom", 8, 3, 19),
  };
}
function Legend({ mode }) {
  const matched = mode === "matchedWithPPR";
  const ends =
    mode === "rentals"
      ? ["€800 or less", "€4,000+"]
      : mode === "shares"
      ? ["€200 or less", "€1,500+"]
      : ["€200k or less", "€850k+"];
  return (
    <details className="map-legend" open>
      <summary>
        {matched ? "Sale compared with asking" : DATASETS[mode].price}
      </summary>
      {matched ? (
        <div className="legend-items">
          {[
            ["#b96923", "Below asking"],
            ["#6555a1", "At asking"],
            ["#2669a5", "Above asking"],
          ].map(([color, label]) => (
            <span key={label}>
              <i style={{ background: color }} />
              {label}
            </span>
          ))}
        </div>
      ) : (
        <>
          <div className="price-gradient" />
          <div className="legend-ends">
            <span>{ends[0]}</span>
            <span>{ends[1]}</span>
          </div>
        </>
      )}
      <span className="legend-unknown">
        <i style={{ background: "#87929f" }} />
        Unavailable
      </span>
      <small>
        At zoom 16 and above, overlapping markers sit side by side. Lines
        indicate their original locations.
      </small>
    </details>
  );
}

export default function Map() {
  const [filters, setFilters] = useState(initialFilters);
  const [draft, setDraft] = useState(filters);
  const [validation, setValidation] = useState("");
  const [view, setView] = useState("map");
  const [sidebar, setSidebar] = useState("filters");
  const [mobileFilters, setMobileFilters] = useState(false);
  const [bounds, setBounds] = useState(null);
  const [result, setResult] = useState({
    features: [],
    loading: true,
    error: "",
  });
  const [retry, setRetry] = useState(0);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const previewTimer = useRef(null);
  const keepPreview = useCallback(() => clearTimeout(previewTimer.current), []);
  const dismissPreview = useCallback(() => {
    clearTimeout(previewTimer.current);
    setHovered(null);
  }, []);
  const hidePreview = useCallback(() => {
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => setHovered(null), 180);
  }, []);
  const showPreview = useCallback((feature, position) => {
    clearTimeout(previewTimer.current);
    setHovered({ feature, position });
  }, []);
  useEffect(() => () => clearTimeout(previewTimer.current), []);
  useEffect(() => {
    dismissPreview();
  }, [view, dismissPreview]);
  const [cache, setCache] = useState({});
  const [sort, setSort] = useState("default");
  const [page, setPage] = useState(1);
  const mapNode = useRef(null);
  const mapRef = useRef(null);
  const resultButton = useRef(null);
  const viewButton = useRef(null);
  const filtersButton = useRef(null);
  const initial = useRef(initialView());
  const remember = useCallback(
    (id, data) => setCache((previous) => ({ ...previous, [id]: data })),
    []
  );
  const closeDetails = () => {
    setSelected(null);
    viewButton.current?.focus();
  };

  useEffect(() => {
    const map = L.map(mapNode.current, {
      center: [initial.current.lat, initial.current.lng],
      zoom: initial.current.zoom,
      minZoom: 3,
      maxZoom: 19,
    });
    mapRef.current = map;
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    const moved = () => {
      if (!mapNode.current?.clientWidth) return;
      const b = map.getBounds(),
        c = map.getCenter();
      const nextBounds = {
        minLat: b.getSouth(),
        maxLat: b.getNorth(),
        minLng: b.getWest(),
        maxLng: b.getEast(),
        lat: c.lat,
        lng: c.lng,
        zoom: map.getZoom(),
      };
      setBounds((previous) =>
        previous &&
        Object.keys(nextBounds).every(
          (key) => previous[key] === nextBounds[key]
        )
          ? previous
          : nextBounds
      );
    };
    map.on("moveend", moved);
    const resizeObserver = new ResizeObserver(() => {
      if (mapNode.current?.clientWidth) map.invalidateSize();
    });
    resizeObserver.observe(mapNode.current);
    moved();
    return () => {
      resizeObserver.disconnect();
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!bounds) return;
    const query = new URLSearchParams({
      lat: bounds.lat,
      lng: bounds.lng,
      zoom: bounds.zoom,
      counties: JSON.stringify(filters.filterCounties),
      agents: JSON.stringify(filters.filterAgents),
      propertytypes: JSON.stringify(filters.filterPropertyTypes),
      startyear: filters.startYear,
      endyear: filters.endYear,
      minbeds: filters.minBeds,
      maxbeds: filters.maxBeds,
      minprice: filters.minPrice,
      maxprice: filters.maxPrice,
      dataoption: filters.dataOption,
    });
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}?${query}`
    );
    const controller = new AbortController();
    setResult({ features: [], loading: true, error: "" });
    setPage(1);
    const timer = setTimeout(() => {
      const query = queryForFilters(filters);
      for (const key of ["minLat", "maxLat", "minLng", "maxLng"])
        query.set(key, bounds[key]);
      fetchJSON(
        `${API}/api/map/list/${DATASETS[filters.dataOption].path}?${query}`,
        controller.signal
      )
        .then((data) => {
          const features = validFeatures(data);
          if (!controller.signal.aborted)
            setResult({
              features,
              loading: false,
              error: "",
              loadedAt: new Date(),
              skipped: data.features.length - features.length,
            });
        })
        .catch(() => {
          if (!controller.signal.aborted)
            setResult({
              features: [],
              loading: false,
              error:
                "We couldn’t load properties for this area. Please try again.",
            });
        });
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [bounds, filters, retry]);

  useEffect(() => {
    if (view !== "map") return;
    const timer = setTimeout(() => mapRef.current?.invalidateSize(), 0);
    return () => clearTimeout(timer);
  }, [view]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    dismissPreview();
    const layer = L.layerGroup().addTo(map);
    const markerSize = Math.max(3, Math.min(18, map.getZoom() - 1));
    const positions = spreadPoints(
      result.features.map((feature) => {
        const [lng, lat] = feature.geometry.coordinates;
        return map.project([lat, lng], map.getZoom());
      }),
      map.getZoom()
    );
    result.features.forEach((feature, index) => {
      const p = feature.properties;
      const [lng, lat] = feature.geometry.coordinates;
      const original = L.latLng(lat, lng);
      const position = map.unproject(
        L.point(positions[index].x, positions[index].y),
        map.getZoom()
      );
      if (positions[index].displaced) {
        L.polyline([original, position], {
          color: "#536b82",
          weight: 1,
          opacity: 0.65,
          clickable: false,
        }).addTo(layer);
      }
      const isSelected = selected?.properties._id === p._id;
      const color =
        filters.dataOption === "matchedWithPPR"
          ? comparisonColor(p.under_pc)
          : priceColor(p.price, filters.dataOption);
      const label =
        filters.dataOption === "matchedWithPPR"
          ? comparisonLabel(p.under_pc)
          : money(p.price);
      const marker = L.marker(position, {
        keyboard: true,
        icon: L.divIcon({
          className: `property-marker ${isSelected ? "selected" : ""}`,
          html: `<span style="background:${color};--marker-size:${
            markerSize + (isSelected ? 4 : 0)
          }px"></span>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
      })
        .on("mouseover", () => showPreview(feature, position))
        .on("mouseout", hidePreview)
        .on("click", () => {
          dismissPreview();
          setSelected(feature);
        })
        .addTo(layer);
      // Leaflet 0.7 exposes its focusable marker element through _icon.
      marker._icon.setAttribute(
        "aria-label",
        `Property ${index + 1}: ${label}`
      );
      marker._icon.addEventListener("focus", () =>
        showPreview(feature, position)
      );
      marker._icon.addEventListener("blur", hidePreview);
    });
    const selectedIndex = result.features.findIndex(
      (feature) => feature === selected
    );
    if (selectedIndex >= 0) {
      const position = positions[selectedIndex];
      L.circleMarker(
        map.unproject(L.point(position.x, position.y), map.getZoom()),
        {
          radius: markerSize / 2 + 5,
          color: "#142b47",
          weight: 3,
          fill: false,
          clickable: false,
        }
      ).addTo(layer);
    }
    return () => map.removeLayer(layer);
  }, [
    result.features,
    selected,
    filters.dataOption,
    bounds?.zoom,
    dismissPreview,
    hidePreview,
    showPreview,
  ]);

  const shown = useMemo(() => {
    const items = result.features.map((feature) => ({
      feature,
      number: result.features.indexOf(feature) + 1,
    }));
    if (sort !== "default")
      items.sort((a, b) => {
        const key =
          filters.dataOption === "matchedWithPPR" ? "under_pc" : "price";
        const av = numeric(a.feature.properties[key]),
          bv = numeric(b.feature.properties[key]);
        if (av === null) return bv === null ? 0 : 1;
        if (bv === null) return -1;
        return sort === "ascending" ? av - bv : bv - av;
      });
    return items;
  }, [result.features, sort, filters.dataOption]);

  function apply(next = draft) {
    const error = validateFilters(next);
    setValidation(error);
    if (error) return;
    setSelected(null);
    setFilters({ ...next });
    setCache({});
    setMobileFilters(false);
    filtersButton.current?.focus();
    setSidebar("results");
  }
  function removeAppliedFilters(patch) {
    setDraft((previous) => ({ ...previous, ...patch }));
    setFilters((previous) => ({ ...previous, ...patch }));
    setValidation("");
    setSelected(null);
    setCache({});
  }
  const list = (
    <section className="results" aria-label="Property results">
      <div className="section-heading">
        <h2>Properties in this area</h2>
      </div>
      <p className="muted">
        {shown.length.toLocaleString("en-IE")} loaded records. Open a property
        for its address and details.
      </p>
      <label className="sort-label" htmlFor={`sort-${view}`}>
        Sort by
        <select
          id={`sort-${view}`}
          value={sort}
          onChange={(e) => {
            setSort(e.target.value);
            setPage(1);
          }}
        >
          <option value="default">Default order</option>
          <option value="ascending">
            {filters.dataOption === "matchedWithPPR"
              ? "Difference: low to high"
              : "Price: low to high"}
          </option>
          <option value="descending">
            {filters.dataOption === "matchedWithPPR"
              ? "Difference: high to low"
              : "Price: high to low"}
          </option>
        </select>
      </label>
      {shown.slice((page - 1) * 30, page * 30).map(({ feature, number }) => {
        const p = feature.properties,
          details = cache[p._id];
        return (
          <button
            className={`result-card ${selected === feature ? "active" : ""}`}
            key={`${p._id}-${number}`}
            onClick={() => setSelected(feature)}
          >
            <span className="eyebrow">Property {number}</span>
            <strong>
              {filters.dataOption === "matchedWithPPR"
                ? comparisonLabel(p.under_pc)
                : money(p.price)}
            </strong>
            <span className="address">
              {details
                ? addressFor(details)
                : "View address and property details"}
            </span>
            {details && (
              <small>
                {[
                  details.property_type,
                  numeric(details.beds) !== null
                    ? `${details.beds} beds`
                    : null,
                  details.county,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </small>
            )}
          </button>
        );
      })}
      {shown.length > 30 && (
        <nav className="pagination" aria-label="Results pages">
          <button disabled={page === 1} onClick={() => setPage((n) => n - 1)}>
            Previous
          </button>
          <span>
            {page} / {Math.ceil(shown.length / 30)}
          </span>
          <button
            disabled={page * 30 >= shown.length}
            onClick={() => setPage((n) => n + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </section>
  );

  return (
    <div className="app-shell">
      <header className="app-header">
        <a className="brand" href="/" aria-label="Extended PPR home">
          <span className="brand-mark">E</span>
          <span>
            Extended PPR<small>Explore Ireland’s property history</small>
          </span>
        </a>
        <nav aria-label="View">
          {["map", "list", "trends"].map((item) => (
            <button
              ref={view === item ? viewButton : null}
              key={item}
              aria-pressed={view === item}
              className={view === item ? "active" : ""}
              onClick={() => {
                setView(item);
                setMobileFilters(false);
              }}
            >
              {item === "map" ? "Map" : item === "list" ? "List" : "Trends"}
            </button>
          ))}
        </nav>
        <button
          ref={filtersButton}
          className="mobile-filter-button"
          aria-expanded={mobileFilters}
          aria-controls="filter-sidebar"
          onClick={() => {
            setMobileFilters(!mobileFilters);
            setSidebar("filters");
          }}
        >
          Filters
        </button>
      </header>
      <div className="workspace">
        <aside
          id="filter-sidebar"
          className={`sidebar ${mobileFilters ? "mobile-open" : ""}`}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setMobileFilters(false);
              filtersButton.current?.focus();
            }
          }}
        >
          <div className="sidebar-tabs">
            <button
              aria-pressed={sidebar === "filters"}
              onClick={() => setSidebar("filters")}
            >
              Filters
            </button>
            <button
              ref={resultButton}
              aria-pressed={sidebar === "results"}
              onClick={() => setSidebar("results")}
            >
              Results ({result.features.length})
            </button>
          </div>
          {sidebar === "filters" ? (
            <Filter
              filters={draft}
              onChange={(key, value) =>
                setDraft((previous) => ({ ...previous, [key]: value }))
              }
              onApply={() => apply()}
              onReset={() => {
                setDraft({ ...DEFAULT_FILTERS });
                setValidation("");
              }}
              error={validation}
            />
          ) : view !== "list" ? (
            list
          ) : (
            <p className="sidebar-note">
              Results are shown in the main panel. Select a property to see its
              details.
            </p>
          )}
          <div className="data-note">
            <h3>About the data</h3>
            <p>
              Historical records, not current availability. Map locations are
              approximate.
            </p>
            <p>
              Map responses may be sampled or limited to 1,000 records. Zoom in
              to explore a smaller area.
            </p>
            <p>Source update date is not provided by the API.</p>
            <a href="https://github.com/extendedppr/eppr-web">
              About this project ↗
            </a>
          </div>
          <button
            className="mobile-close"
            onClick={() => {
              setMobileFilters(false);
              filtersButton.current?.focus();
            }}
          >
            Close filters
          </button>
        </aside>
        <main
          className={`main-panel ${view === "trends" ? "trends-view" : ""}`}
        >
          <div className="results-bar">
            <div>
              <h1>{DATASETS[filters.dataOption].label}</h1>
              <p>
                {filters.filterCounties.includes("all")
                  ? "All counties"
                  : filters.filterCounties
                      .map(
                        (c) => counties.find((o) => o.value === c)?.label || c
                      )
                      .join(", ")}{" "}
                · {filters.startYear}–{filters.endYear}
              </p>
            </div>
            <span role="status">
              {view === "trends"
                ? "All areas matching filters"
                : result.loading
                ? "Loading properties…"
                : result.error
                ? "Unable to load"
                : `${result.features.length.toLocaleString(
                    "en-IE"
                  )} records loaded`}
            </span>
          </div>
          <ActiveFilters filters={filters} onRemove={removeAppliedFilters} />
          <div
            className={`results-content ${view === "map" ? "map-results" : ""}`}
          >
            {view !== "trends" && (
              <div className="status-area" aria-live="polite">
                {result.error && (
                  <div className="error" role="alert">
                    {result.error}{" "}
                    <button onClick={() => setRetry((n) => n + 1)}>
                      Try again
                    </button>
                  </div>
                )}
                {!result.loading &&
                  !result.error &&
                  !result.features.length && (
                    <p>
                      No properties found. Try a wider year or price range, or
                      move the map.
                    </p>
                  )}
                {result.features.length >= 1000 && (
                  <p>
                    Showing {result.features.length.toLocaleString("en-IE")}{" "}
                    loaded records; more may exist in this area. Zoom in or
                    narrow your filters.
                  </p>
                )}
                {result.skipped > 0 && (
                  <p>
                    {result.skipped} records could not be mapped because their
                    coordinates were invalid.
                  </p>
                )}
              </div>
            )}
            <div className="map-stage" hidden={view !== "map"}>
              <div
                ref={mapNode}
                id="map"
                aria-label="Map of property records"
              />
              <Legend mode={filters.dataOption} />
              {view === "map" && hovered && (
                <PropertyPreview
                  key={`${filters.dataOption}-${hovered.feature.properties._id}`}
                  feature={hovered.feature}
                  position={hovered.position}
                  map={mapRef.current}
                  mode={filters.dataOption}
                  cachedData={cache[hovered.feature.properties._id]}
                  onLoaded={remember}
                  onEnter={keepPreview}
                  onLeave={hidePreview}
                  onDismiss={dismissPreview}
                  onOpen={() => {
                    setSelected(hovered.feature);
                    dismissPreview();
                  }}
                />
              )}
            </div>
            {view === "list" && <div className="list-view">{list}</div>}
            {view === "trends" && (
              <AnalyticsModal key={JSON.stringify(filters)} filters={filters} />
            )}
            {selected && view !== "trends" && (
              <PropertyDetails
                key={`${filters.dataOption}-${selected.properties._id}`}
                feature={selected}
                cachedData={cache[selected.properties._id]}
                mode={filters.dataOption}
                onClose={closeDetails}
                onLoaded={remember}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
